// src/Components/Streak/StreakWeekStrip.tsx
// The last seven local days: a flame on done days, an ice crystal on frozen
// days, a quiet dot on missed days and a ring around today. In the celebration
// today's dot lights up after `lightTodayAfter` seconds.
import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Icon from "../Ui/Icon";
import { DayStatus, WeekDay } from "./streakModel";

interface Props {
  days: WeekDay[];
  /** Celebration: show today as pending first, then light it after this many seconds. */
  lightTodayAfter?: number;
  className?: string;
}

const STATUS_LABEL: Record<DayStatus, string> = {
  done: "streak day",
  frozen: "kept by a freeze",
  missed: "no activity",
  pending: "today, not yet",
  none: "",
};

const StreakWeekStrip: React.FC<Props> = ({ days, lightTodayAfter, className = "" }) => {
  const reduce = useReducedMotion();
  const [lit, setLit] = useState(lightTodayAfter == null);

  useEffect(() => {
    if (lightTodayAfter == null) return undefined;
    if (reduce) {
      setLit(true);
      return undefined;
    }
    const t = window.setTimeout(() => setLit(true), lightTodayAfter * 1000);
    return () => window.clearTimeout(t);
  }, [lightTodayAfter, reduce]);

  return (
    <ol className={`streak-week ${className}`} aria-label="This week">
      {days.map((d) => {
        const status: DayStatus = d.isToday && d.status === "done" && !lit ? "pending" : d.status;
        const popping = d.isToday && d.status === "done" && lightTodayAfter != null && lit && !reduce;
        return (
          <li
            key={d.day}
            className={`streak-week__day is-${status} ${d.isToday ? "is-today" : ""}`}
            aria-label={`${d.weekday}${STATUS_LABEL[status] ? `, ${STATUS_LABEL[status]}` : ""}`}
            data-status={status}
          >
            <motion.span
              className="streak-week__dot"
              initial={false}
              animate={popping ? { scale: [0.6, 1.35, 1] } : { scale: 1 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
            >
              {status === "done" ? <Icon name="fire" size={20} filled /> : null}
              {status === "frozen" ? <Icon name="snowflake" size={16} /> : null}
              {status === "done" && popping ? <span className="streak-week__ring" /> : null}
            </motion.span>
            <span className="streak-week__label">{d.weekday.slice(0, 2)}</span>
          </li>
        );
      })}
    </ol>
  );
};

export default StreakWeekStrip;
